import { supabase } from '@/integrations/supabase/client';

let stripePromise = null;

// Dynamic import for Stripe to handle build optimization
const loadStripeLibrary = async () => {
  try {
    const { loadStripe } = await import('@stripe/stripe-js');
    return loadStripe;
  } catch (error) {
    console.error('Error loading Stripe library:', error);
    return null;
  }
};

// Initialize Stripe with publishable key from admin settings
export const initializeStripe = async () => {
  if (stripePromise) return stripePromise;

  try {
    const { data, error } = await supabase
      .from('admin_settings')
      .select('setting_value')
      .eq('setting_key', 'stripe_publishable_key')
      .single();

    if (error) throw error;

    if (data?.setting_value) {
      const loadStripe = await loadStripeLibrary();
      if (loadStripe) {
        stripePromise = loadStripe(data.setting_value);
      }
    }
    
    return stripePromise;
  } catch (error) {
    console.error('Error initializing Stripe:', error);
    return null;
  }
};

// Fallback constants ONLY — the source of truth is `subscription_plans`
// in the DB (columns: stripe_price_id_monthly / stripe_price_id_yearly).
// When Stripe products are created via createStripeProducts() below the
// real price IDs are persisted back to that table + admin_settings.
export const STRIPE_PRICE_IDS = {
  starter_monthly: null,       // Starter = $0 = no Stripe price needed
  starter_yearly: null,
  pro_trader_monthly: null,    // filled by createStripeProducts()
  pro_trader_yearly: null,
  institutional_ai_monthly: null,
  institutional_ai_yearly: null,
  enterprise_monthly: null,    // custom pricing / contact sales
  enterprise_yearly: null,
};

// Auto-create Stripe products and prices
export const createStripeProducts = async () => {
  try {
    const { data: secretKey } = await supabase
      .from('admin_settings')
      .select('setting_value')
      .eq('setting_key', 'stripe_secret_key')
      .single();

    if (!secretKey?.setting_value) {
      throw new Error('Stripe secret key not configured');
    }

    // Prices MUST match subscription_plans (see migration
    // 20260720140000_align_plans_with_landing.sql) AND the landing page.
    // Amounts are in cents (Stripe convention).
    // Starter ($0) is not a Stripe product — free plan is DB-only.
    const products = [
      {
        name: 'Snap Chart AI — Pro Trader',
        description: 'Unlimited analyses + Counterfactual capital tracking + TDI + Multi-chart + Auto-saved journal.',
        prices: {
          monthly: { amount: 4900,  interval: 'month' },   // $49
          yearly:  { amount: 29000, interval: 'year'  },   // $290 (2 months free vs 12×49)
        },
        metadata: { plan: 'pro_trader' },
      },
      {
        name: 'Snap Chart AI — Institutional',
        description: 'Everything in Pro + Institutional Risk Officer + Impulse-trader prevention + Capital protection + Priority support.',
        prices: {
          monthly: { amount: 14900,  interval: 'month' },  // $149
          yearly:  { amount: 149000, interval: 'year'  },  // $1490 (2 months free)
        },
        metadata: { plan: 'institutional_ai' },
      },
    ];

    const createdProducts = [];

    for (const product of products) {
      // Create product in Stripe
      const productResponse = await fetch('https://api.stripe.com/v1/products', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${secretKey.setting_value}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: product.name,
          description: product.description,
          'metadata[plan]': product.metadata.plan
        })
      });

      const createdProduct = await productResponse.json();
      
      if (!productResponse.ok) {
        throw new Error(`Failed to create product: ${createdProduct.error?.message}`);
      }

      // Create monthly price
      const monthlyPriceResponse = await fetch('https://api.stripe.com/v1/prices', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${secretKey.setting_value}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          product: createdProduct.id,
          unit_amount: product.prices.monthly.amount.toString(),
          currency: 'usd',
          'recurring[interval]': product.prices.monthly.interval,
          'metadata[plan]': product.metadata.plan,
          'metadata[billing]': 'monthly'
        })
      });

      const monthlyPrice = await monthlyPriceResponse.json();
      
      if (!monthlyPriceResponse.ok) {
        throw new Error(`Failed to create monthly price: ${monthlyPrice.error?.message}`);
      }

      // Create yearly price
      const yearlyPriceResponse = await fetch('https://api.stripe.com/v1/prices', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${secretKey.setting_value}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          product: createdProduct.id,
          unit_amount: product.prices.yearly.amount.toString(),
          currency: 'usd',
          'recurring[interval]': product.prices.yearly.interval,
          'metadata[plan]': product.metadata.plan,
          'metadata[billing]': 'yearly'
        })
      });

      const yearlyPrice = await yearlyPriceResponse.json();
      
      if (!yearlyPriceResponse.ok) {
        throw new Error(`Failed to create yearly price: ${yearlyPrice.error?.message}`);
      }

      createdProducts.push({
        product: createdProduct,
        monthlyPrice: monthlyPrice,
        yearlyPrice: yearlyPrice
      });

      // Store price IDs in admin settings
      await supabase.from('admin_settings').upsert([
        { 
          setting_key: `stripe_price_${product.metadata.plan}_monthly`, 
          setting_value: monthlyPrice.id 
        },
        { 
          setting_key: `stripe_price_${product.metadata.plan}_yearly`, 
          setting_value: yearlyPrice.id 
        }
      ], { onConflict: 'setting_key' });
    }

    return createdProducts;

  } catch (error) {
    console.error('Error creating Stripe products:', error);
    throw error;
  }
};

// Get stored price IDs from admin settings
export const getStoredPriceIds = async () => {
  try {
    const { data, error } = await supabase
      .from('admin_settings')
      .select('setting_key, setting_value')
      .like('setting_key', 'stripe_price_%');

    if (error) throw error;

    const priceIds = {};
    data?.forEach(setting => {
      const key = setting.setting_key.replace('stripe_price_', '');
      priceIds[key] = setting.setting_value;
    });

    return priceIds;
  } catch (error) {
    console.error('Error getting stored price IDs:', error);
    return {};
  }
};

// Create Stripe checkout session
export const createCheckoutSession = async (priceId, userId, userEmail) => {
  try {
    const { data: secretKey } = await supabase
      .from('admin_settings')
      .select('setting_value')
      .eq('setting_key', 'stripe_secret_key')
      .single();

    if (!secretKey?.setting_value) {
      throw new Error('Stripe secret key not configured');
    }

    const response = await fetch('https://api.stripe.com/v1/checkout/sessions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${secretKey.setting_value}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        'payment_method_types[]': 'card',
        'line_items[0][price]': priceId,
        'line_items[0][quantity]': '1',
        mode: 'subscription',
        success_url: `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/pricing`,
        customer_email: userEmail,
        'metadata[user_id]': userId,
        'subscription_data[metadata][user_id]': userId
      })
    });

    const session = await response.json();
    
    if (!response.ok) {
      throw new Error(session.error?.message || 'Failed to create checkout session');
    }

    return session;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

// Redirect to Stripe Checkout
export const redirectToCheckout = async (priceId, userId, userEmail) => {
  try {
    const stripe = await initializeStripe();
    if (!stripe) {
      throw new Error('Stripe not initialized. Please configure Stripe keys in admin panel.');
    }

    const session = await createCheckoutSession(priceId, userId, userEmail);
    
    const { error } = await stripe.redirectToCheckout({
      sessionId: session.id
    });

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Error redirecting to checkout:', error);
    throw error;
  }
};

// Create Stripe Customer Portal session
export const createPortalSession = async (customerId) => {
  try {
    const { data: secretKey } = await supabase
      .from('admin_settings')
      .select('setting_value')
      .eq('setting_key', 'stripe_secret_key')
      .single();

    if (!secretKey?.setting_value) {
      throw new Error('Stripe secret key not configured');
    }

    const response = await fetch('https://api.stripe.com/v1/billing_portal/sessions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${secretKey.setting_value}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        customer: customerId,
        return_url: `${window.location.origin}/dashboard`
      })
    });

    const session = await response.json();
    
    if (!response.ok) {
      throw new Error(session.error?.message || 'Failed to create portal session');
    }

    return session;
  } catch (error) {
    console.error('Error creating portal session:', error);
    throw error;
  }
};

// Get user subscription details
export const getUserSubscription = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting user subscription:', error);
    return null;
  }
};

// Cancel subscription
export const cancelSubscription = async (subscriptionId) => {
  try {
    const { data: secretKey } = await supabase
      .from('admin_settings')
      .select('setting_value')
      .eq('setting_key', 'stripe_secret_key')
      .single();

    if (!secretKey?.setting_value) {
      throw new Error('Stripe secret key not configured');
    }

    const response = await fetch(`https://api.stripe.com/v1/subscriptions/${subscriptionId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${secretKey.setting_value}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        prorate: 'true'
      })
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error?.message || 'Failed to cancel subscription');
    }

    return result;
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};